import { useState } from 'react';
import { Check, EyeOff, Flag, FlagOff, Gavel, RotateCcw, ShieldX } from 'lucide-react';

import { REJECT_REASONS, type ModerationAction } from '@/client/api/assets/moderation';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { Button } from '@/components/ui/button';
import ModerationDialog from './ModerationDialog';

/** Review state of one photo and the decisions an admin can take on it. */
const AssetModerationPanel = ({ asset }: { asset: Asset }) => {
  const { hasPermission, isSuperAdmin } = useHasPermission('asset.moderate');
  const [action, setAction] = useState<ModerationAction | null>(null);
  const m = asset.moderation;
  const held = m?.status === 'rejected' || m?.status === 'hidden';
  const allowed = hasPermission || isSuperAdmin;

  return (
    <div>
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Gavel className="h-4 w-4" /> Review
      </h4>
      <div className="space-y-1 text-sm">
        {m?.status ? (
          <p>
            <span className="font-medium capitalize">{m.status}</span>
            {m.decided_at && <span className="text-gray-500"> · {new Date(m.decided_at).toLocaleString()}</span>}
            {m.reason_code && <span className="block text-gray-600">{REJECT_REASONS[m.reason_code] ?? m.reason_code}</span>}
            {m.note && <span className="block text-gray-500">“{m.note}”</span>}
          </p>
        ) : (
          <p className="text-gray-500">Not reviewed.</p>
        )}
        {m?.flagged === 1 && <p className="text-blue-700">Flagged by staff: {m.flag_note}</p>}
      </div>
      {allowed && (
        <div className="mt-3 flex flex-wrap gap-2">
          {held ? (
            <Button size="sm" variant="outline" onClick={() => setAction('restore')}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore
            </Button>
          ) : (
            <>
              {m?.status !== 'approved' && (
                <Button size="sm" variant="outline" onClick={() => setAction('approve')}>
                  <Check className="mr-1.5 h-3.5 w-3.5" /> Approve
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setAction('hide')}>
                <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Hide
              </Button>
              <Button size="sm" variant="outline" className="text-red-700" onClick={() => setAction('reject')}>
                <ShieldX className="mr-1.5 h-3.5 w-3.5" /> Reject
              </Button>
            </>
          )}
          {m?.flagged === 1 ? (
            <Button size="sm" variant="ghost" onClick={() => setAction('unflag')}>
              <FlagOff className="mr-1.5 h-3.5 w-3.5" /> Clear flag
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setAction('flag')}>
              <Flag className="mr-1.5 h-3.5 w-3.5" /> Flag
            </Button>
          )}
        </div>
      )}
      <ModerationDialog action={action} asids={asset.asid != null ? [asset.asid] : []} onClose={() => setAction(null)} />
    </div>
  );
};

export default AssetModerationPanel;
